import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { pandimLines } from '@/mocks/pandim';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

export default function PandimPage() {
  const [activeTab, setActiveTab] = useState(pandimLines[0].id);

  useSEO({
    title: 'Pandim Caruaru PE | Mesas e Estações de Trabalho – Home Office Premium',
    description: 'Pandim em Caruaru PE. Mesas corporativas, estações de trabalho e móveis de aço para escritórios modernos. Revendedor autorizado com entrega e suporte técnico.',
    keywords: 'Pandim Caruaru, mesas corporativas Pernambuco, estações de trabalho, móveis escritório',
    path: '/pandim',
    ogTitle: 'Pandim Caruaru PE | Home Office Premium',
    ogDescription: 'Revendedor autorizado Pandim em Caruaru PE. Mesas corporativas e estações de trabalho.',
  });

  useSchemaLD({
    id: 'schema-pandim',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Pandim Caruaru PE – Home Office Premium',
      description: 'Revendedor autorizado Pandim em Caruaru PE. Mesas executivas, estações de trabalho modulares e móveis de aço para escritórios modernos.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/pandim`,
      about: {
        '@type': 'Brand',
        name: 'Pandim',
        description: 'Referência em mobiliário corporativo. Mesas executivas, estações de trabalho e móveis de aço com qualidade e design.',
      },
    },
  });

  useSchemaLD({
    id: 'schema-pandim-breadcrumb',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com' },
        { '@type': 'ListItem', position: 2, name: 'Marcas', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/marcas` },
        { '@type': 'ListItem', position: 3, name: 'Pandim', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/pandim` },
      ],
    },
  });

  useSchemaLD({
    id: 'schema-pandim-faq',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Onde comprar móveis Pandim em Caruaru e Pernambuco?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Home Office Premium é revendedora autorizada Pandim em Caruaru PE, atendendo todo o estado de Pernambuco. Temos mesas executivas, estações de trabalho e móveis de aço Pandim com entrega e montagem inclusa.' },
        },
        {
          '@type': 'Question',
          name: 'Quais produtos Pandim estão disponíveis na Home Office Premium?',
          acceptedAnswer: { '@type': 'Answer', text: 'Trabalhamos com toda a linha Pandim: mesas executivas, estações de trabalho modulares e móveis de aço. Todos os produtos estão disponíveis para pronta entrega ou sob encomenda em Caruaru e região.' },
        },
        {
          '@type': 'Question',
          name: 'A montagem dos móveis Pandim está inclusa?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. A Home Office Premium inclui montagem e instalação em todos os projetos. Nossa equipe técnica realiza a montagem no local, garantindo perfeito acabamento e alinhamento dos móveis Pandim.' },
        },
        {
          '@type': 'Question',
          name: 'A Home Office Premium atende projetos Pandim fora de Caruaru?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Atendemos todo o estado de Pernambuco, incluindo Recife, Petrolina, Garanhuns, Arcoverde, Serra Talhada e demais cidades do Agreste, Sertão e Zona da Mata.' },
        },
      ],
    },
  });

  const activeLine = pandimLines.find(l => l.id === activeTab);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 h-[520px] md:h-[600px] overflow-hidden bg-gray-900">
        <img
          src="/media/ia/0220.webp"
          alt="Pandim"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[#374151] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-star-fill text-xs"></i>
                Marca Parceira Oficial
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                Pandim
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Referência em mobiliário corporativo. Mesas executivas, estações de trabalho e móveis de aço com qualidade, funcionalidade e design para escritórios modernos.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.pandim.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Ver Catálogo Completo
                </a>
                <a
                  href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#374151] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sobre */}
      <section className="py-20 bg-[#f5f5f0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">Quem é a Pandim</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Soluções completas em mobiliário corporativo de alto padrão
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                A Pandim é referência no mercado brasileiro de mobiliário corporativo, oferecendo soluções completas em mesas executivas, estações de trabalho modulares e móveis de aço para escritórios modernos.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                Somos revendedores autorizados da Pandim em Caruaru e região, com toda a linha disponível para pronta entrega ou sob encomenda, com montagem inclusa.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Garantia de Qualidade' },
                  { icon: 'ri-truck-line', label: 'Entrega em todo Brasil' },
                  { icon: 'ri-tools-line', label: 'Montagem Inclusa' },
                  { icon: 'ri-award-line', label: 'Revendedor Autorizado' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-gray-200 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-gray-700 text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '3 linhas', label: 'Categorias de Produto' },
                { value: 'Modular', label: 'Sistema de Montagem' },
                { value: 'Aço', label: 'Móveis Reforçados' },
                { value: 'BR', label: 'Fabricação Nacional' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-2xl p-6 text-center border border-gray-100">
                  <p className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Linhas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Produtos Pandim</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Selecione uma linha para ver detalhes. Clique em "Ver no site oficial" para explorar todos os modelos.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {pandimLines.map(line => (
              <button
                key={line.id}
                onClick={() => setActiveTab(line.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === line.id
                    ? 'bg-[#374151] text-white'
                    : 'bg-[#f5f5f0] text-gray-600 hover:bg-gray-200'
                }`}
              >
                {line.name}
              </button>
            ))}
          </div>

          {activeLine && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className={`grid gap-4 ${activeLine.photos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {activeLine.photos.map((photo, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl overflow-hidden bg-[#f8f8f6] flex items-center justify-center p-4 ${
                      activeLine.photos.length === 1 ? 'h-[480px]' : 'h-64'
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`${activeLine.name} ${idx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">{activeLine.category}</p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{activeLine.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">{activeLine.description}</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={activeLine.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#374151] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i>
                    Ver no site Pandim
                  </a>
                  <a
                    href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#f5f5f0] text-gray-700 text-sm font-medium px-6 py-3 rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    Pedir orçamento
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-4">Revendedor Autorizado</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Quer equipar seu escritório com<br className="hidden md:block" /> móveis Pandim?
          </h2>
          <p className="text-gray-400 text-sm mb-10 max-w-lg mx-auto">
            Fale com nossa equipe e receba um orçamento personalizado. Atendemos empresas e escritórios em Caruaru e região.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#374151] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
            <a href="https://www.pandim.com.br" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-external-link-line"></i>
              Ver Catálogo Oficial
            </a>
            <Link to="/contato"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-mail-line"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
